import { useCallback, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';

import { PendingChangesActionType } from 'constants/pendingChanges';

import {
  selectExtendedBorrowers,
  selectExtendedSubjectProperty,
  selectOrderedNonBorrowersTableData,
  selectUnderwritingExpirationDates,
} from 'store/loanApplications/selectors';
import { selectApplicationConsentRecords } from 'store/loanApplications/selectors/loanConsents.selectors';

import { usePrepareDisclosuresWizard } from '../../../../../hooks';
import { IntentToProceedStatus } from '../../borrowersDelivery.constants';
import {
  isBorrowersDeliveryValid,
  seedBorrowersDelivery,
  selectOrderedSigners,
  setSignerDelivery,
  summarizeDelivery,
} from '../../borrowersDelivery.utils';
import { estimateEarliestClosing } from '../../ecdEstimate.utils';

import { resolvePropertyZip } from './utils';

const useBorrowersDelivery = () => {
  const { borrowersDelivery, setBorrowersDelivery, setDirty, isMailProviderEnabled } =
    usePrepareDisclosuresWizard();

  const extendedBorrowers = useSelector(selectExtendedBorrowers);
  const nonBorrowers = useSelector(selectOrderedNonBorrowersTableData);
  const consentRecords = useSelector(selectApplicationConsentRecords);
  const expirationDates = useSelector(selectUnderwritingExpirationDates);
  const subjectProperty = useSelector(selectExtendedSubjectProperty);

  const propertyZip = useMemo(() => resolvePropertyZip(subjectProperty), [subjectProperty]);
  const sendDate = useMemo(() => new Date(), []);

  const seedInput = useMemo(
    () => ({
      borrowers: extendedBorrowers ?? [],
      nonBorrowers: (nonBorrowers ?? []).filter(
        ({ pendingChangesActionType }) =>
          pendingChangesActionType !== PendingChangesActionType.DELETED,
      ),
      consentRecords,
      propertyZip,
    }),
    [extendedBorrowers, nonBorrowers, consentRecords, propertyZip],
  );

  useEffect(() => {
    if (
      !borrowersDelivery.isSeeded &&
      (seedInput.borrowers.length || seedInput.nonBorrowers.length)
    ) {
      setBorrowersDelivery(seedBorrowersDelivery(seedInput));
    }
  }, [borrowersDelivery.isSeeded, seedInput, setBorrowersDelivery]);

  const signers = useMemo(() => selectOrderedSigners(borrowersDelivery), [borrowersDelivery]);
  const summary = useMemo(() => summarizeDelivery(borrowersDelivery), [borrowersDelivery]);
  const isValid = useMemo(() => isBorrowersDeliveryValid(borrowersDelivery), [borrowersDelivery]);

  const updateDelivery = useCallback(
    (signerKey, deliveryMethod) => {
      setBorrowersDelivery(state => setSignerDelivery(state, signerKey, deliveryMethod));
      setDirty(true);
    },
    [setBorrowersDelivery, setDirty],
  );

  const ecdEstimate = useMemo(
    () =>
      estimateEarliestClosing({
        baseEarliestClosingDate: expirationDates?.earliestClosingDate,
        sendDate,
        anyMail: summary.anyMail,
      }),
    [expirationDates, sendDate, summary.anyMail],
  );

  return {
    signers,
    summary,
    isValid,
    ecdEstimate,
    updateDelivery,
    propertyZip,
    isMailProviderEnabled,
    intentToProceed: { status: IntentToProceedStatus.NOT_YET_CAPTURED },
  };
};

export default useBorrowersDelivery;
